
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import { map } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';

import { cacheable } from '@app/utils/rxjs-functions';

import { ExpiringCache, User } from '../models';

import { AuthService } from './auth.service';
import { SharedUtilsService } from './shared-utils.service';

export interface UserApi {
  items: User[];
  total_count: number;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private currentAccountBeingViewed: User;
  public passwordPattern = "^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=?!*]).*$";
  public passwordPatternDescription = "Not valid, passwords must be at least 6 characters AND contain at least 1 lowercase, 1 uppercase AND also at least one character from the set @#$%^&+=";

  usersStorage: ExpiringCache = new ExpiringCache();

  private API_URL = '/api/users/';
  private headerOptions = {headers: new HttpHeaders({'Content-Type': 'application/json'})};

  // System Admin not listed
  public userTypes = ['Applicant', 'Member', 'System Admin'];
  public accessTypes = ['Admin', 'Edit', 'View', 'TST', 'TST Admin'];   

  constructor(
    private httpClient: HttpClient,
    private formBuilder: UntypedFormBuilder,
    private _sharedUtilsService: SharedUtilsService,
    private _authService: AuthService
  ) { }


  setCurrentUserBeingViewed(_usr: User): void {
    this.currentAccountBeingViewed = _usr;
  }


  getCurrentUserBeingViewed(): User {
    return this.currentAccountBeingViewed;
  }


  getCurrentlyLoggedInUser(): User {
    return this._authService.getCurrentlyLoggedInUser();
  }


  getCurrentUsersBaseRoute() {
    const currentUser = this._authService.getCurrentlyLoggedInUser();
    return this.getRouteForUser(currentUser.userType);
  }


  updateUserFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<User[]> {
    const updateRequest = {ids: arrayOfIds, fields: fieldsToUpdate};
    return this.httpClient.put<User[]>(this.API_URL + 'update-certain-fields-array/', JSON.stringify(updateRequest), this.headerOptions);
  }


  getAll(): Observable<User[]> {
    // smarter than just going to api every time. Getting them all, so just cache for use in dropdowns all over
    if (this._sharedUtilsService.isStorageValid(this.usersStorage)) {
      // console.log('Saved an API call for users. Retreived from cache.');
      return this.usersStorage.itemCache;
    }

    this.usersStorage = this._sharedUtilsService.updateStorageExpiration(this.usersStorage);
    // console.log('Set users storage to: ', this.usersStorage);
    return this.usersStorage.itemCache = cacheable<any>(this.httpClient.get<User[]>(this.API_URL));
  }


  create(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL, JSON.stringify(user), this.headerOptions);
  }


  activate(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'activate', JSON.stringify(user), this.headerOptions);
  }


  createUserForApplicant(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'createUserForApplicant', JSON.stringify(user), this.headerOptions);
  }


  createTempUserAccount(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'createTempUserAccount', JSON.stringify(user), this.headerOptions);
  }


  checkUserSession(): Observable<any> {
    return this.httpClient.get(this.API_URL + 'user-session');
  }


  register(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'register', JSON.stringify(user), this.headerOptions);
  }


  forgotPasswordRequest(user): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'forgot-password', JSON.stringify(user), this.headerOptions);
  }


  passwordResetRequest(user): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'password-reset', JSON.stringify(user), this.headerOptions);
  }


  getUser(id: any): Observable<User> {
    return this.httpClient.get<User>(this.API_URL + id);
  }


  getUserByEmail(_email: any): Observable<User> {
    return this.httpClient.get<User>(this.API_URL + 'by-email/' + _email);
  }


  delete(id: string): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + 'mark-deleted/' + id, null, this.headerOptions);
  }


  updateUser(user: User): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + user._id, JSON.stringify(user), this.headerOptions);
  }


  updateUserForApplicant(user: User): Observable<User> {
    return this.httpClient.put<User>(this.API_URL + 'updateUserForApplicant/' + user._id, JSON.stringify(user), this.headerOptions);
  }


  completeRegistration(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'completeRegistration', JSON.stringify(user), this.headerOptions);
  }


  tempLogin(user: User): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'templogin', JSON.stringify(user), this.headerOptions);
  }


  emailCheck(searchTerms): Observable<User[]> {
    return this.httpClient.post<User[]>(this.API_URL + 'email-check', JSON.stringify(searchTerms), this.headerOptions);
  }


  search(searchTerms): Observable<User[]> {
    return this.httpClient.post<User[]>(this.API_URL + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  updateFieldsById(_id, fieldsToUpdate): Observable<User[]> {
    return this.httpClient.put<User[]>(this.API_URL + 'update-certain-fields/' + _id, JSON.stringify(fieldsToUpdate), this.headerOptions);
  }


  login(user: User) {
    return this.httpClient.post<any>(this.API_URL + 'login', JSON.stringify(user), this.headerOptions);
  }


  logout() {
    return this.httpClient.get(this.API_URL + 'logout', this.headerOptions).pipe(
      map((res) => {
        // remove user from local storage to log user out
        localStorage.removeItem('currentUser');
        localStorage.removeItem('jwt_token');
        // res.json();
      })
    );    
  }


  getUserRolesAndAccess(_userId: string): Observable<User> {
    return this.httpClient.get<User>(this.API_URL + _userId + '/complete-roles-info');
  }


  getUsersForRole(_roleId: string): Observable<User[]> {
    return this.httpClient.get<User[]>(this.API_URL + 'role/' + _roleId);
  }


  selectAllSearch(searchTerms: User): Observable<User[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<User[]>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCustomDatasource(searchParams, sortField = 'last_name', sortDirection = 'desc', pageNumber: number = 0, pageSize: number = 10): Observable<UserApi> {
    searchParams['selectAll'] = false;

    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;

    return this.httpClient.post<UserApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions); 
  }


  // ─── Email/Phone Verification ───────────────────────────────────────────────

  requestEmailChange(userId: string, newEmail: string): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'request-email-change', JSON.stringify({ userId, newEmail }), this.headerOptions);
  }

  verifyEmailChange(userId: string, code: string): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'verify-email-change', JSON.stringify({ userId, code }), this.headerOptions);
  }

  requestPhoneChange(userId: string, newPhone: string): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'request-phone-change', JSON.stringify({ userId, newPhone }), this.headerOptions);
  }

  verifyPhoneChange(userId: string, code: string): Observable<User> {
    return this.httpClient.post<User>(this.API_URL + 'verify-phone-change', JSON.stringify({ userId, code }), this.headerOptions);
  }


  userFormGroup(formData: User) {
    return this.formBuilder.group({
        userId: [formData.userId],
        email: [formData.email, Validators.required],
        password: [formData.password],
        userType: [formData.userType, Validators.required],
        first_name: [formData.first_name, Validators.required],
        last_name: [formData.last_name, Validators.required],
        phone: [formData.phone, Validators.required],
        address: [formData.address, Validators.required],
        city: [formData.city, Validators.required],
        state: [formData.state, Validators.required],
        zip: [formData.zip, Validators.required],
        active: [formData.active],
        confirmed: [formData.confirmed]
    });
  }


  isSuperAdmin(_user) {
    let isSuper = false;

    if (_user.userType === 'System Admin') {
      isSuper = true;
    }

    return isSuper;
  }


  checkForAdminPrivileges(_user) {
    let hasPrivelages = false;

    if (_user.userType === 'System Admin') {
      hasPrivelages = true;
    }

    return hasPrivelages;
  }


  getRouteForUser(userType) {
    let userPortal = '';

    if (userType === 'System Admin' || userType === 'DOE') {
      userPortal = '/admin';
    }

    return userPortal;
  }
}
